// src/components/layout/MainLayout.jsx
// -----------------------------------------------------------------------------
// Shell layout wrapping every authenticated page: Sidebar on the left,
// Header on top, page content in the middle (via <Outlet />), Footer at
// the bottom. Used once in App.jsx as the parent route for all protected
// pages, so individual pages only need to render their own content.
// -----------------------------------------------------------------------------
import { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import Footer from './Footer';

// Maps a route path prefix to a human-readable header title.
const PAGE_TITLES = {
  '/dashboard': 'Dashboard',
  '/tickets': 'Tickets',
  '/ticket-status-history': 'Status History',
  '/ticket-link-types': 'Ticket Link Types',
  '/projects': 'Projects',
  '/project-phases': 'Project Phases',
  '/users': 'Users',
  '/roles': 'Roles',
  '/client-organizations': 'Client Organizations',
  '/ticket-types': 'Ticket Types',
  '/notifications': 'Notifications',
};

function getTitleForPath(pathname) {
  const match = Object.keys(PAGE_TITLES).find((prefix) => pathname.startsWith(prefix));
  return match ? PAGE_TITLES[match] : 'AFix';
}

export default function MainLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <div className="app-shell">
      {/* Mobile backdrop overlay */}
      {mobileOpen && (
        <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} />
      )}

      <Sidebar collapsed={collapsed} mobileOpen={mobileOpen} onClose={() => setMobileOpen(false)} />

      <div className="main-column">
        <Header
          title={getTitleForPath(location.pathname)}
          onToggleSidebar={() => {
            if (window.innerWidth <= 900) {
              setMobileOpen((o) => !o);
            } else {
              setCollapsed((c) => !c);
            }
          }}
        />

        <div className="page-content">
          {/* Each matched child route renders here - see routes in App.jsx */}
          <Outlet />
        </div>

        <Footer />
      </div>
    </div>
  );
}
